import useAuth from "../../../hooks/useAuth";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Sidebar } from "../../../widgets/layout/sidebar";
import { Header } from "../../../widgets/layout/header";
import { Footer } from "../../../widgets/layout/footer";
import { AppShell } from "../../../widgets/layout/app-shell";
import { Loading } from "../shared/loading/Loading";
import { resolvePrivateLayoutRoute } from "./config/layoutRoutes";
import { RolesEnum } from "../../../helpers/GlobalEnum";
import { getRoleTitle } from "../../../entities/user/model/getRoleTitle";
import {
    ensureSsoSessionAlive,
    keycloak,
} from "../../../shared/auth/lib/keycloakClient";
import {
    getAppsPortalUrl,
    isAppsPortalRedirectEnabled,
    redirectToAppsPortalIfEnabled,
} from "../../../shared/auth/lib/appsPortal";
import { authService } from "../../../helpers/services/Auth";

const MOBILE_BREAKPOINT = 992;
const LoadingIndicator = () => <Loading fullScreen text="Cargando..." />;

export const PrivateLayout = () => {
    const { auth, loading, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [isMobile, setIsMobile] = useState(() => window.innerWidth <= MOBILE_BREAKPOINT);
    const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
    const [ssoExchangeFailed, setSsoExchangeFailed] = useState(false);
    const redirectTriggeredRef = useRef(false);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        if (loading || auth?.id) {
            redirectTriggeredRef.current = false;
            setSsoExchangeFailed(false);
            return;
        }

        if (redirectTriggeredRef.current) {
            return;
        }
        redirectTriggeredRef.current = true;

        logout();

        if (keycloak.authenticated) {
            console.error(
                "Sesión institucional válida pero el backend no entregó tokens de aplicación."
            );
            setSsoExchangeFailed(true);
            return;
        }

        // Sin sesión y con el portal centralizado activo: la autenticación no
        // se resuelve aquí, se delega al portal de apps. Se evalúa después de
        // `keycloak.authenticated` a propósito: si la sesión SSO es válida y lo
        // que falló fue el intercambio con el backend, salir al portal solo
        // produciría un ciclo (portal -> app -> portal).
        if (redirectToAppsPortalIfEnabled()) {
            return;
        }

        const redirectUri = `${window.location.origin}${location.pathname}${location.search}`;
        const failLogin = (error) => {
            console.error("No fue posible iniciar sesión institucional:", error);
        };

        try {
            Promise.resolve(keycloak.login({ redirectUri })).catch(failLogin);
        } catch (error) {
            // keycloak.init() nunca corrió: adapter indefinido, falla síncrona.
            failLogin(error);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, auth?.id, location.pathname, location.search]);

    const isSidebarOpen = isMobile ? isMobileSidebarOpen : isDesktopSidebarOpen;

    const handleSidebarToggle = (nextOpen) => {
        if (isMobile) {
            setIsMobileSidebarOpen(nextOpen);
            return;
        }
        setIsDesktopSidebarOpen(nextOpen);
    };

    const handleMobileMenuToggle = () => {
        setIsMobileSidebarOpen((prev) => !prev);
    };

    const closeMobileSidebar = () => {
        setIsMobileSidebarOpen(false);
    };

    const handleLogout = () => {
        navigate("/admin/logout");
    };

    const handleEditProfile = () => {
        navigate("/admin/edit-user");
    };

    const handleEditAnyUser = () => {
        navigate("/admin/management");
    };

    // Reintento explícito, disparado por el usuario (nunca automático): si
    // hay un access_token de Keycloak en memoria, reintenta SOLO el
    // intercambio contra el backend (útil para fallas transitorias como un
    // 502 del JWKS) sin navegar fuera de la app. Solo si no hay token en
    // memoria vuelve a redirigir a Keycloak.
    const handleRetrySsoExchange = async () => {
        if (!keycloak.token) {
            redirectTriggeredRef.current = false;
            setSsoExchangeFailed(false);

            if (redirectToAppsPortalIfEnabled()) {
                return;
            }

            const redirectUri = `${window.location.origin}${location.pathname}${location.search}`;
            keycloak.login({ redirectUri }).catch((error) => {
                console.error("No fue posible reintentar el login institucional:", error);
            });
            return;
        }

        try {
            // El access_token de Keycloak dura pocos minutos: si la pantalla de
            // error lleva rato abierta, hay que renovarlo antes de mandarlo al
            // backend o el intercambio falla por token vencido.
            await ensureSsoSessionAlive();

            const response = await authService.loginWithKeycloak(keycloak.token);
            if (response?.access && response?.refresh) {
                setSsoExchangeFailed(false);
                redirectTriggeredRef.current = false;
                window.dispatchEvent(new Event("authUpdated"));
            }
        } catch (error) {
            console.error("Reintento de intercambio con Keycloak falló:", error);
        }
    };

    // Cierre de sesión desde la pantalla de error: no puede delegar en
    // /admin/logout porque, sin `auth.id`, este layout no renderiza su Outlet.
    const handleSsoLogout = () => {
        logout();

        const redirectUri = isAppsPortalRedirectEnabled()
            ? getAppsPortalUrl()
            : window.location.origin;

        keycloak.logout({ redirectUri });
    };

    const routeLayout = resolvePrivateLayoutRoute(location.pathname);
    const contentMode = routeLayout?.contentMode || "legacy";

    const headerProps = {
        isMobile,
        isSidebarOpen,
        onMenuToggle: handleMobileMenuToggle,
        userAuth: auth,
        userRoleLabel: getRoleTitle(auth?.rol_id),
        onLogout: handleLogout,
        onEditProfile: handleEditProfile,
        onEditAnyUser:
            auth?.rol_id === RolesEnum.ADMIN ? handleEditAnyUser : undefined,
        showUserMenu: true,
        withSidebar: true,
    };

    const headerNode =
        typeof routeLayout?.renderHeader === "function"
            ? routeLayout.renderHeader(headerProps)
            : <Header {...headerProps} />;

    const footerNode =
        typeof routeLayout?.renderFooter === "function"
            ? routeLayout.renderFooter({ userAuth: auth, isMobile })
            : <Footer />;

    if (loading) {
        return <LoadingIndicator />;
    }

    if (!auth?.id) {
        if (ssoExchangeFailed) {
            return (
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "1rem",
                        height: "100vh",
                        textAlign: "center",
                        padding: "2rem",
                    }}
                >
                    <h5>No fue posible iniciar sesión</h5>
                    <p className="text-muted" style={{ maxWidth: 420 }}>
                        Su sesión institucional es válida, pero el sistema no
                        pudo completar el inicio de sesión. Contacte al
                        administrador si el problema persiste.
                    </p>
                    <div style={{ display: "flex", gap: "0.75rem" }}>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleRetrySsoExchange}
                        >
                            Reintentar
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={handleSsoLogout}
                        >
                            Cerrar sesión
                        </button>
                    </div>
                </div>
            );
        }
        return <LoadingIndicator />;
    }

    return (
        <AppShell
            isDesktopSidebarOpen={isDesktopSidebarOpen}
            isMobile={isMobile}
            isMobileSidebarOpen={isMobileSidebarOpen}
            onCloseMobile={closeMobileSidebar}
            contentMode={contentMode}
            header={headerNode}
            sidebar={(
                <Sidebar
                    userAuth={auth}
                    isOpen={isSidebarOpen}
                    isMobile={isMobile}
                    onToggle={handleSidebarToggle}
                    onCloseMobile={closeMobileSidebar}
                />
            )}
            footer={footerNode}
        >
            <Outlet context={{ userAuth: auth }} />
        </AppShell>
    );
};
